import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Send, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useSendInvoiceEmail } from '../api/hooks';

interface SendInvoiceEmailDialogProps {
  invoiceId: string;
  defaultEmail?: string | null;
  clientName?: string | null;
  getAccessToken: () => Promise<string>;
  onClose: () => void;
}

import { isValidEmail } from '@/lib/validation';

export function SendInvoiceEmailDialog({
  invoiceId,
  defaultEmail,
  clientName,
  getAccessToken,
  onClose,
}: Readonly<SendInvoiceEmailDialogProps>) {
  const { t } = useTranslation();
  const [to, setTo] = useState(defaultEmail || '');
  const [message, setMessage] = useState('');
  const sendEmail = useSendInvoiceEmail(getAccessToken);

  const handleSend = useCallback(() => {
    sendEmail.mutate(
      { invoiceId, to, message: message || undefined },
      {
        onSuccess: () => {
          toast.success(t('email.sent'));
          onClose();
        },
        onError: () => toast.error(t('email.sendFailed')),
      },
    );
  }, [invoiceId, to, message, sendEmail, onClose, t]);

  return (
    <div className="space-y-4 p-4" data-testid="send-invoice-email-dialog">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">{t('invoice.sendEmailTitle')}</h3>
        <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close">
          ✕
        </Button>
      </div>

      {clientName && (
        <p className="text-sm text-muted-foreground">
          {t('invoice.sendingTo', { name: clientName })}
        </p>
      )}

      <div className="space-y-2">
        <label className="text-sm font-medium">{t('email.recipientEmail')}</label>
        <Input
          type="email"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder={t('email.enterEmail')}
          data-testid="invoice-email-to"
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium">{t('invoice.sendEmailMessage')}</label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={t('invoice.sendEmailPlaceholder')}
          className="w-full min-h-[100px] rounded-md border border-input bg-background px-3 py-2 text-sm"
          data-testid="invoice-email-message"
        />
      </div>

      <div className="flex gap-2 justify-end">
        <Button variant="ghost" onClick={onClose} data-testid="invoice-email-cancel">
          {t('invoice.cancel')}
        </Button>
        <Button
          onClick={handleSend}
          disabled={!isValidEmail(to) || sendEmail.isPending}
          data-testid="invoice-email-send"
        >
          {sendEmail.isPending ? (
            <Loader2 className="mr-1 h-4 w-4 animate-spin" />
          ) : (
            <Send className="mr-1 h-4 w-4" />
          )}
          {sendEmail.isPending ? t('email.sending') : t('email.send')}
        </Button>
      </div>
    </div>
  );
}
